// src/Pages/EventPersonOversigt/EventPersonOversigt.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Accordion,
    Badge,
    Button,
    Card,
    Container,
    Divider,
    Group,
    Loader,
    Paper,
    SegmentedControl,
    Stack,
    Text,
    ThemeIcon,
    Title,
} from "@mantine/core";
import {
    IconCalendar,
    IconFileText,
    IconClock,
    IconExternalLink,
    IconCar,
    IconTicket,
    IconInfoCircle,
} from "@tabler/icons-react";

type Language = "en" | "da";

type ShiftDto = {
    shiftId: number;
    title: string;
    date: string;
    start: string;
    end: string;
    status?: string;
    functionId?: number;
    functionName?: string;
    eventId?: number;
};

type PersonDto = {
    personId: number;
    firstName: string;
    lastName: string;
};

type EventDto = {
    eventId: number;
    eventName: string;
    infoText?: string;
};

type TicketDto = {
    hasTicket: boolean;
    ticketID?: number;
    ticketDescription?: string;
    ticketTimeStart?: string;
    ticketTimeEnd?: string;
    active?: boolean;
    isUsed?: boolean;
    usedAt?: string | null;
    sourceType?: string;
};

type MyShiftApiItem = {
    shiftId: number;
    functionId?: number;
    start: string;
    end: string;
    status?: string;
    title?: string;
    eventId?: number;
    functionName?: string;
};

type MyShiftResponse = {
    confirmed?: MyShiftApiItem[];
    waitlist?: MyShiftApiItem[];
    stats?: {
        confirmedCount?: number;
        waitlistCount?: number;
    };
};

type DriverRequestDto = {
    transportRequestId: number;
    eventId: number;
    kind?: number;
    contactPerson?: string;
    contactPersonEmail?: string;
    contactPersonMobile?: string;
    numberOfPersons?: number;
    numberOfLuggage?: number;
    scheduledDate?: string;
    scheduledTime?: string;
    flightNo?: string;
    pickUpPlace?: string;
    dropOffPlace?: string;
    description?: string;
    note?: string;
    statusTransportId?: number;
    vehicleId?: number;
    vehicleName?: string;
    vehiclePlate?: string;
    currentDriverStatusId?: number;
    currentDriverStatusAt?: string;
    currentDriverStatusNote?: string;
};

type EventFileDto = {
    fileID: number;
    eventID: number;
    eventFileSectionID?: number | null;
    name: string;
    originalFileName: string;
    sizeBytes: number;
    sortOrder: number;
    downloadUrl: string;
};

type EventFileSectionDto = {
    eventFileSectionID: number;
    eventID: number;
    name: string;
    description?: string | null;
    sortOrder: number;
    isActive: boolean;
    isPublic: boolean;
    files: EventFileDto[];
};
function formatBytes(bytes: number) {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const v = bytes / Math.pow(k, i);
    return `${v.toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
}

function formatDate(date: string, lang: Language) {
    if (!date) return "";
    const locale = lang === "da" ? "da-DK" : "en-GB";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return date;

    return new Intl.DateTimeFormat(locale, {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(d);
}

const texts = {
    da: {
        invalidLink: "Ugyldigt link eller bruger ikke fundet.",
        loading: "Loader…",
        eventId: "EventID",
        personId: "PersonID",
        myPage: "Min side",
        shifts: "Mine vagter",
        ticket: "Mad",
        noTicket: "Ingen billetoplysninger fundet.",
        documents: "Infomation",
        noDocuments: "Ingen dokumenter på eventet.",
        open: "Åbn",
        info: "Info",
        importantInfo: "Vigtig information",
        transport: "Transport",
        transportDescription: "Opret transport-booking for denne person på dette event.",
        createTransport: "Opret transport",
        mobileTip: "Mobil-tip: Denne side er lavet som kort + accordion, så den er nem på telefon.",
        languageDa: "Dansk",
        languageEn: "English",
        driverRides: "Mine køreture",
        from: "Fra",
        to: "Til",
        vehicle: "Bil",
        flight: "Flight",
        note: "Note",
        unknownContact: "Ukendt kontakt",
        noFilesInSection: "Ingen filer i denne sektion.",
        noTicketFound: "Ingen billet fundet.",
        source: "Kilde",
        used: "Brugt",
        active: "Aktiv",
        airport: "Lufthavn",
        onTheWay: "På vej",
        atPickup: "Ved pickup",
        pickedUp: "Samlet op",
        delivered: "Afleveret",
        planned: "Planlagt",
    },
    en: {
        invalidLink: "Invalid link or logged-in user not found.",
        loading: "Loading…",
        eventId: "Event ID",
        personId: "Person ID",
        myPage: "My page",
        shifts: "My shifts",
        ticket: "Meals",
        noTicket: "No ticket information found.",
        documents: "Infomation",
        noDocuments: "No event documents.",
        open: "Open",
        info: "Info",
        importantInfo: "Important information",
        transport: "Transport",
        transportDescription: "Create a transport booking for this person on this event.",
        createTransport: "Create transport",
        mobileTip: "Mobile tip: This page uses cards + accordion, so it works well on phones.",
        languageDa: "Dansk",
        languageEn: "English",
        driverRides: "My rides",
        from: "From",
        to: "To",
        vehicle: "Vehicle",
        flight: "Flight",
        note: "Note",
        unknownContact: "Unknown contact",
        noFilesInSection: "No files in this section.",
        noTicketFound: "No ticket found.",
        source: "Source",
        used: "Used",
        active: "Active",
        airport: "Airport",
        onTheWay: "On the way",
        atPickup: "At pickup",
        pickedUp: "Picked up",
        delivered: "Delivered",
        planned: "Planned",
    },
};

export default function EventPersonOversigt() {
    const apiUrl = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();
    const { eventId, personId } = useParams();

    const [language, setLanguage] = useState<Language>("en");
    const t = texts[language];

    const eid = Number(eventId);
    const pid = Number(personId)

    const [loading, setLoading] = useState(true);

    const [eventInfo, setEventInfo] = useState<EventDto | null>(null);
    const [personInfo, setPersonInfo] = useState<PersonDto | null>(null);
    const [fileSections, setFileSections] = useState<EventFileSectionDto[]>([]);
    const [shifts, setShifts] = useState<ShiftDto[]>([]);
    const [ticketInfo, setTicketInfo] = useState<TicketDto | null>(null);

    const [canManageTransport, setCanManageTransport] = useState(false);
    const [transportLoading, setTransportLoading] = useState(false);
    const [transportError, setTransportError] = useState<string | null>(null);

    const [driverRequests, setDriverRequests] = useState<DriverRequestDto[]>([]);

    const shiftsSorted = useMemo(() => {
        return [...shifts].sort((a, b) => {
            const da = `${a.date} ${a.start}`;
            const db = `${b.date} ${b.start}`;
            return da.localeCompare(db);
        });
    }, [shifts]);

    const hasFunction1Shift = useMemo(() => {
        return shifts.some((x) => x.functionId === 1);
    }, [shifts]);

    const activeDriverRequests = useMemo(() => {
        return driverRequests.filter((r) => r.currentDriverStatusId !== 4);
    }, [driverRequests]);

    const showShifts = shiftsSorted.length > 0;
    const showTicket = !!ticketInfo?.hasTicket;
    const showInfo = !!eventInfo?.infoText?.trim();
    const showTransport = hasFunction1Shift && canManageTransport;
    const showDriverRides = driverRequests.length > 0;

    async function loadDriverRequests() {
        if (!apiUrl || !eid || !pid) return;

        try {
            const res = await fetch(
                `${apiUrl}/api/v2/driver-requests/transport/driver/my-requests?personId=${pid}&eventId=${eid}`
            );

            if (!res.ok) {
                setDriverRequests([]);
                return;
            }

            const data = await res.json();
            setDriverRequests(Array.isArray(data) ? data : []);
        } catch {
            setDriverRequests([]);
        }
    }

    async function loadAll() {
        if (!apiUrl || !eid || !pid || Number.isNaN(eid) || Number.isNaN(pid)) {
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            try {
                const canRes = await fetch(
                    `${apiUrl}/api/transport/can-manage?eventId=${eid}&personId=${pid}`
                );
                setCanManageTransport(canRes.ok ? !!(await canRes.json()) : false);
            } catch {
                setCanManageTransport(false);
            }
            await loadDriverRequests();

            try {
                const filesRes = await fetch(
                    `${apiUrl}/api/events/${eid}/file-sections?personId=${pid}`
                );
                const filesJson = filesRes.ok ? await filesRes.json() : [];

                const mappedSections: EventFileSectionDto[] = (filesJson as any[]).map((section) => ({
                    eventFileSectionID:
                        section.eventFileSectionID ?? section.EventFileSectionID,
                    eventID: section.eventID ?? section.EventID,
                    name: section.name ?? section.Name,
                    description: section.description ?? section.Description,
                    sortOrder: section.sortOrder ?? section.SortOrder ?? 0,
                    isActive: section.isActive ?? section.IsActive ?? true,
                    isPublic: section.isPublic ?? section.IsPublic ?? false,
                    files: ((section.files ?? section.Files ?? []) as any[]).map((file) => ({
                        fileID: file.fileID ?? file.FileID,
                        eventID: file.eventID ?? file.EventID,
                        eventFileSectionID:
                            file.eventFileSectionID ?? file.EventFileSectionID,
                        name: file.name ?? file.Name,
                        originalFileName:
                            file.originalFileName ?? file.OriginalFileName,
                        sizeBytes: file.sizeBytes ?? file.SizeBytes,
                        sortOrder: file.sortOrder ?? file.SortOrder ?? 0,
                        downloadUrl: file.downloadUrl ?? file.DownloadUrl,
                    })),
                }));

                setFileSections(
                    mappedSections
                        .filter((section) => section.isActive)
                        .sort(
                            (a, b) =>
                                a.sortOrder - b.sortOrder ||
                                a.eventFileSectionID - b.eventFileSectionID
                        )
                        .map((section) => ({
                            ...section,
                            files: [...section.files].sort(
                                (a, b) => a.sortOrder - b.sortOrder || a.fileID - b.fileID
                            ),
                        }))
                );
            } catch {
                setFileSections([]);
            }

            const [personRes, eventRes] = await Promise.all([
                fetch(`${apiUrl}/api/persons/${pid}`),
                fetch(`${apiUrl}/api/events/${eid}/light`),
            ]);

            if (personRes.ok) {
                const p = await personRes.json();
                setPersonInfo({
                    personId: p.personId ?? p.PersonId ?? pid,
                    firstName: p.firstName ?? p.FirstName ?? "",
                    lastName: p.lastName ?? p.LastName ?? "",
                });
            } else {
                setPersonInfo({ personId: pid, firstName: "", lastName: "" });
            }

            if (eventRes.ok) {
                const e = await eventRes.json();
                setEventInfo({
                    eventId: e.eventId ?? e.EventId ?? e.EventID ?? eid,
                    eventName: e.eventName ?? e.EventName ?? e.name ?? e.Name ?? "Event",
                    infoText: e.infoText ?? e.InfoText ?? e.personInfoText ?? e.PersonInfoText ?? "",
                });
            } else {
                setEventInfo({ eventId: eid, eventName: "Event", infoText: "" });
            }

            try {
                const shiftsRes = await fetch(
                    `${apiUrl}/api/shifts/my?personId=${pid}&eventId=${eid}`
                );

                if (shiftsRes.ok) {
                    const s = (await shiftsRes.json()) as MyShiftResponse;
                    const confirmed = Array.isArray(s?.confirmed) ? s.confirmed : [];

                    const mapped: ShiftDto[] = confirmed.map((x) => {
                        const startDate = x.start ? new Date(x.start) : null;
                        const endDate = x.end ? new Date(x.end) : null;

                        const validStart = startDate && !Number.isNaN(startDate.getTime());
                        const validEnd = endDate && !Number.isNaN(endDate.getTime());

                        return {
                            shiftId: x.shiftId,
                            title: x.title ?? "",
                            date: validStart ? startDate.toISOString().slice(0, 10) : "",
                            start: validStart
                                ? startDate.toLocaleTimeString("da-DK", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                })
                                : "",
                            end: validEnd
                                ? endDate.toLocaleTimeString("da-DK", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                })
                                : "",
                            status: x.status,
                            functionId: x.functionId,
                            functionName: x.functionName,
                            eventId: x.eventId,
                        };
                    });

                    setShifts(mapped);
                } else {
                    setShifts([]);
                }
            } catch {
                setShifts([]);
            }

            try {
                const ticketRes = await fetch(
                    `${apiUrl}/api/tickets/my-ticket?personId=${pid}&eventId=${eid}`
                );

                if (ticketRes.ok) {
                    const ticket = await ticketRes.json();

                    setTicketInfo({
                        hasTicket: ticket.hasTicket ?? false,
                        ticketID: ticket.ticketID,
                        ticketDescription: ticket.ticketDescription,
                        ticketTimeStart: ticket.ticketTimeStart,
                        ticketTimeEnd: ticket.ticketTimeEnd,
                        active: ticket.active,
                        isUsed: ticket.isUsed,
                        usedAt: ticket.usedAt,
                        sourceType: ticket.sourceType,
                    });
                } else {
                    setTicketInfo(null);
                }
            } catch {
                setTicketInfo(null);
            }
        } finally {
            setLoading(false);
        }
    }

    function goToTransportBooking() {
        if (!eid) return;
        navigate(`/transport-route/book?eventId=${eid}`);
    }

    async function createTransportAndGo() {
        if (!apiUrl || !eid || !pid) return;

        setTransportError(null);
        setTransportLoading(true);

        try {
            const res = await fetch(`${apiUrl}/api/transport/admin/tokens`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    eventId: eid,
                    personId: pid,
                    expiresInDays: 30,
                }),
            });

            if (!res.ok) {
                const msg = await res.text();
                throw new Error(msg || "Kunne ikke oprette transport token.");
            }

            const data = await res.json();
            const token = data.token ?? data.Token ?? data?.tokenDto?.token;

            if (!token) throw new Error("API returnerede ikke et token.");

            navigate(`/transport/book?token=${encodeURIComponent(token)}`);
        } catch (err: any) {
            setTransportError(err?.message ?? "Fejl ved oprettelse af transport.");
        } finally {
            setTransportLoading(false);
        }
    }

    function getDriverStatusLabel(statusId?: number, lang: Language = "da") {
        switch (statusId) {
            case 1:
                return lang === "da" ? t.onTheWay : t.onTheWay;
            case 2:
                return lang === "da" ? t.atPickup : t.atPickup;
            case 3:
                return lang === "da" ? t.pickedUp : t.pickedUp;
            case 4:
                return lang === "da" ? t.delivered : t.delivered;
            default:
                return lang === "da" ? t.planned : t.planned;
        }
    }

    async function addDriverLog(requestId: number, statusId: number, note?: string) {
        if (!apiUrl || !pid) return;

        try {
            const res = await fetch(
                `${apiUrl}/api/v2/driver-requests/transport/driver/requests/${requestId}/log`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        statusId,
                        driverId: pid,
                        note: note ?? null,
                    }),
                }
            );

            if (!res.ok) {
                throw new Error("Kunne ikke opdatere driver-log");
            }

            await loadDriverRequests();
        } catch (err) {
            console.error(err);
        }
    }

    function getDriverStatusColor(statusId?: number) {
        switch (statusId) {
            case 1:
                return "blue";
            case 2:
                return "yellow";
            case 3:
                return "orange";
            case 4:
                return "green";
            default:
                return "gray";
        }
    }

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl, eid, pid, language]);

    if (!eid || !pid || Number.isNaN(eid) || Number.isNaN(pid)) {
        return (
            <Container size="sm" py="md">
                <Paper withBorder p="md" radius="md">
                    <Text c="red">{t.invalidLink}</Text>
                </Paper>
            </Container>
        );
    }

    return (
        <Container size="sm" py="md">
            <Stack gap="md">
                <Card withBorder padding="md" radius="md">
                    <Group justify="space-between" align="flex-start">
                        <Stack gap={6}>
                            <Title order={3} style={{ lineHeight: 1.2 }}>
                                {eventInfo?.eventName ?? "Event"}
                            </Title>

                            <Group gap="xs">
                                <Badge color="red" variant="light">
                                    {t.eventId}: {eid}
                                </Badge>
                                <Badge color="red" variant="light">
                                    {t.personId}: {pid}
                                </Badge>
                                <Badge color="red" variant="dot">
                                    {t.myPage}
                                </Badge>
                            </Group>

                            <Text c="dimmed" size="sm">
                                {personInfo
                                    ? `${personInfo.firstName} ${personInfo.lastName}`.trim()
                                    : ""}
                            </Text>
                        </Stack>

                        <SegmentedControl
                            size="xs"
                            value={language}
                            onChange={(value) => setLanguage(value as Language)}
                            data={[
                                { label: t.languageDa, value: "da" },
                                { label: t.languageEn, value: "en" },
                            ]}
                        />
                    </Group>
                </Card>

                {loading ? (
                    <Group>
                        <Loader color="red" size="sm" />
                        <Text>{t.loading}</Text>
                    </Group>
                ) : (
                    <Accordion multiple variant="separated">
                        {showShifts && (
                            <Accordion.Item value="shifts">
                                <Accordion.Control
                                    icon={
                                        <ThemeIcon color="red" variant="light" radius="xl">
                                            <IconClock size={18} />
                                        </ThemeIcon>
                                    }
                                >
                                    {t.shifts}
                                </Accordion.Control>
                                <Accordion.Panel>
                                    <Stack gap="sm">
                                        {shiftsSorted.map((s) => (
                                            <Paper key={s.shiftId} withBorder p="sm" radius="md">
                                                <Group justify="space-between" align="flex-start">
                                                    <div>
                                                        <Text fw={600}>{s.title}</Text>

                                                        <Group gap="xs" mt={4}>
                                                            <IconCalendar size={16} />
                                                            <Text size="sm">
                                                                {formatDate(s.date, language)} · {s.start}–{s.end}
                                                            </Text>
                                                        </Group>

                                                        {s.functionName && (
                                                            <Text size="xs" c="dimmed">
                                                                {s.functionName}
                                                            </Text>
                                                        )}
                                                    </div>

                                                    {s.status && (
                                                        <Badge color="red" variant="light">
                                                            {s.status}
                                                        </Badge>
                                                    )}
                                                </Group>
                                            </Paper>
                                        ))}
                                    </Stack>
                                </Accordion.Panel>
                            </Accordion.Item>
                        )}

                        {showTicket && (
                            <Accordion.Item value="ticket">
                                <Accordion.Control
                                    icon={
                                        <ThemeIcon color="red" variant="light" radius="xl">
                                            <IconTicket size={18} />
                                        </ThemeIcon>
                                    }
                                >
                                    {t.ticket}
                                </Accordion.Control>
                                <Accordion.Panel>
                                    {!ticketInfo || !ticketInfo.hasTicket ? (
                                        <Text c="dimmed">{t.noTicketFound}</Text>
                                    ) : (
                                        <Stack gap="sm">
                                            <Paper withBorder p="sm" radius="md">
                                                <Group justify="space-between" align="flex-start" wrap="nowrap">
                                                    <div style={{ minWidth: 0 }}>
                                                        <Text fw={600} lineClamp={2}>
                                                            {ticketInfo.ticketDescription || "-"}
                                                        </Text>

                                                        <Text size="sm" c="dimmed" mt={4}>
                                                            {ticketInfo.ticketTimeStart && ticketInfo.ticketTimeEnd
                                                                ? `${new Date(ticketInfo.ticketTimeStart).toLocaleTimeString("da-DK", {
                                                                    hour: "2-digit",
                                                                    minute: "2-digit",
                                                                })} - ${new Date(ticketInfo.ticketTimeEnd).toLocaleTimeString("da-DK", {
                                                                    hour: "2-digit",
                                                                    minute: "2-digit",
                                                                })}`
                                                                : "-"}
                                                        </Text>

                                                        {ticketInfo.sourceType && (
                                                            <Text size="xs" c="dimmed" mt={4}>
                                                                {t.source}: {ticketInfo.sourceType}
                                                            </Text>
                                                        )}
                                                    </div>

                                                    <Badge color={ticketInfo.isUsed ? "gray" : "red"} variant="light">
                                                        {ticketInfo.isUsed ? t.used : t.active}
                                                    </Badge>
                                                </Group>
                                            </Paper>
                                        </Stack>
                                    )}
                                </Accordion.Panel>
                            </Accordion.Item>
                        )}

                        <Accordion.Item value="files">
                            <Accordion.Control
                                icon={
                                    <ThemeIcon color="red" variant="light" radius="xl">
                                        <IconFileText size={18} />
                                    </ThemeIcon>
                                }
                            >
                                {t.documents}
                            </Accordion.Control>
                            <Accordion.Panel>
                                {fileSections.length === 0 ? (
                                    <Text c="dimmed">{t.noDocuments}</Text>
                                ) : (
                                    <Stack gap="md">
                                        {fileSections.map((section) => (
                                            <Paper key={section.eventFileSectionID} withBorder p="md" radius="md">
                                                <Stack gap="sm">
                                                    <div>
                                                        <Text fw={700}>{section.name}</Text>

                                                        {section.description && (
                                                            <Text size="sm" c="dimmed" mt={4}>
                                                                {section.description}
                                                            </Text>
                                                        )}
                                                    </div>

                                                    {section.files.length === 0 ? (
                                                        <Text size="sm" c="dimmed" ml="md">
                                                            {t.noFilesInSection}
                                                        </Text>
                                                    ) : (
                                                        <Stack gap="xs" ml="md">
                                                            {section.files.map((f) => (
                                                                <Paper
                                                                    key={f.fileID}
                                                                    withBorder
                                                                    p="sm"
                                                                    radius="md"
                                                                    bg="gray.0"
                                                                >
                                                                    <Group
                                                                        justify="space-between"
                                                                        wrap="nowrap"
                                                                        align="flex-start"
                                                                    >
                                                                        <div style={{ minWidth: 0 }}>
                                                                            <Text fw={600} lineClamp={2}>
                                                                                {f.name}
                                                                            </Text>
                                                                            <Text size="xs" c="dimmed" lineClamp={1}>
                                                                                {f.originalFileName} · {formatBytes(f.sizeBytes)}
                                                                            </Text>
                                                                        </div>

                                                                        <Button
                                                                            color="red"
                                                                            variant="light"
                                                                            size="xs"
                                                                            rightSection={<IconExternalLink size={16} />}
                                                                            component="a"
                                                                            href={`${apiUrl}/api/events/${eid}/files/${f.fileID}/open`}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                        >
                                                                            {t.open}
                                                                        </Button>
                                                                    </Group>
                                                                </Paper>
                                                            ))}
                                                        </Stack>
                                                    )}
                                                </Stack>
                                            </Paper>
                                        ))}
                                    </Stack>
                                )}
                            </Accordion.Panel>
                        </Accordion.Item>

                        {showInfo && (
                            <Accordion.Item value="info">
                                <Accordion.Control
                                    icon={
                                        <ThemeIcon color="red" variant="light" radius="xl">
                                            <IconInfoCircle size={18} />
                                        </ThemeIcon>
                                    }
                                >
                                    {t.info}
                                </Accordion.Control>
                                <Accordion.Panel>
                                    <Paper withBorder p="sm" radius="md">
                                        <Text fw={600} mb={6}>
                                            {t.importantInfo}
                                        </Text>
                                        <Text style={{ whiteSpace: "pre-wrap" }}>
                                            {eventInfo?.infoText}
                                        </Text>
                                    </Paper>
                                </Accordion.Panel>
                            </Accordion.Item>
                        )}

                        {showTransport && (
                            <Accordion.Item value="transport">
                                <Accordion.Control
                                    icon={
                                        <ThemeIcon color="red" variant="light" radius="xl">
                                            <IconCar size={18} />
                                        </ThemeIcon>
                                    }
                                >
                                    {t.transport}
                                </Accordion.Control>
                                <Accordion.Panel>
                                    <Stack gap="sm">
                                        <Text c="dimmed" size="sm">
                                            {t.transportDescription}
                                        </Text>

                                        {transportError && (
                                            <Paper withBorder p="sm" radius="md">
                                                <Text c="red" size="sm">
                                                    {transportError}
                                                </Text>
                                            </Paper>
                                        )}

                                        <Group justify="flex-end">
                                            <Button
                                                color="red"
                                                onClick={goToTransportBooking}
                                                loading={transportLoading}
                                                variant="light"
                                            >
                                                {t.createTransport}
                                            </Button>
                                        </Group>
                                    </Stack>
                                </Accordion.Panel>
                            </Accordion.Item>
                        )}

                        {showDriverRides && (
                            <Accordion.Item value="driver-rides">
                                <Accordion.Control
                                    icon={
                                        <ThemeIcon color="red" variant="light" radius="xl">
                                            <IconCar size={18} />
                                        </ThemeIcon>
                                    }
                                >
                                    {t.driverRides}
                                </Accordion.Control>
                                <Accordion.Panel>
                                    <Stack gap="md">
                                        {activeDriverRequests.map((r) => {
                                            const pickupText =
                                                r.pickUpPlace && r.pickUpPlace.trim()
                                                    ? r.pickUpPlace
                                                    : r.kind === 1
                                                        ? t.airport
                                                        : "-";

                                            const dropOffText =
                                                r.dropOffPlace && r.dropOffPlace.trim()
                                                    ? r.dropOffPlace
                                                    : r.kind === 2
                                                        ? t.airport
                                                        : "-";

                                            return (
                                                <Paper key={r.transportRequestId} withBorder p="md" radius="md">
                                                    <Stack gap="sm">
                                                        <Group justify="space-between" align="flex-start">
                                                            <div>
                                                                <Text fw={700}>{r.contactPerson || t.unknownContact}</Text>
                                                                <Text size="sm" c="dimmed">
                                                                    {r.scheduledDate || ""} {r.scheduledTime || ""}
                                                                </Text>
                                                            </div>

                                                            <Badge
                                                                color={getDriverStatusColor(r.currentDriverStatusId)}
                                                                variant="light"
                                                            >
                                                                {getDriverStatusLabel(r.currentDriverStatusId, language)}
                                                            </Badge>
                                                        </Group>

                                                        <Text size="sm">
                                                            <strong>{t.from}:</strong> {pickupText}
                                                        </Text>

                                                        <Text size="sm">
                                                            <strong>{t.to}:</strong> {dropOffText}
                                                        </Text>

                                                        {r.flightNo && (
                                                            <Text size="sm">
                                                                <strong>{t.flight}:</strong> {r.flightNo}
                                                            </Text>
                                                        )}

                                                        {(r.vehicleName || r.vehiclePlate) && (
                                                            <Text size="sm">
                                                                <strong>{t.vehicle}:</strong> {r.vehicleName || "-"}
                                                                {r.vehiclePlate ? ` (${r.vehiclePlate})` : ""}
                                                            </Text>
                                                        )}

                                                        {r.note && (
                                                            <Text size="sm">
                                                                <strong>{t.note}:</strong> {r.note}
                                                            </Text>
                                                        )}

                                                        <Group gap="xs" mt="xs">
                                                            <Button
                                                                size="xs"
                                                                variant="light"
                                                                onClick={() => addDriverLog(r.transportRequestId, 1)}
                                                            >
                                                                {t.onTheWay}
                                                            </Button>

                                                            <Button
                                                                size="xs"
                                                                variant="light"
                                                                color="yellow"
                                                                onClick={() => addDriverLog(r.transportRequestId, 2)}
                                                            >
                                                                {t.atPickup}
                                                            </Button>

                                                            <Button
                                                                size="xs"
                                                                variant="light"
                                                                color="orange"
                                                                onClick={() => addDriverLog(r.transportRequestId, 3)}
                                                            >
                                                                {t.pickedUp}
                                                            </Button>

                                                            <Button
                                                                size="xs"
                                                                variant="light"
                                                                color="green"
                                                                onClick={() => addDriverLog(r.transportRequestId, 4)}
                                                            >
                                                                {t.delivered}
                                                            </Button>
                                                        </Group>
                                                    </Stack>
                                                </Paper>
                                            );
                                        })}
                                    </Stack>
                                </Accordion.Panel>
                            </Accordion.Item>
                        )}
                    </Accordion>
                )}

                <Divider />
                <Text size="xs" c="dimmed">
                    {t.mobileTip}
                </Text>
            </Stack>
        </Container>
    );
}